import { createBrowserRouter } from 'react-router-dom';
import { RootLayout } from '@/layouts/RootLayout';
import { BoundaryLayout } from '@/layouts/BoundaryLayout';
import { RouteError } from '@/components/RouteError';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { ClientLayout } from '@/layouts/ClientLayout';
import { SettingsLayout } from '@/layouts/SettingsLayout';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { ClientsPage } from '@/pages/ClientsPage';
import { CreateClientPage } from '@/pages/CreateClientPage';
import { EditClientPage } from '@/pages/EditClientPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Application router (RR7 data router).
 *
 * Layout mapping from the old Remix flat-file tree:
 *   root.tsx          → RootLayout (providers live in App.tsx)
 *   _boundary.tsx     → BoundaryLayout (toasts, transitions; errorElement)
 *   _boundary._index  → HomePage
 *
 * Routes are ported feature-by-feature. `$param` → `:param`; `_index` → index;
 * resource routes (loader/action only) become API functions, not routes.
 */
export const router = createBrowserRouter([
    {
        element: <RootLayout />,
        // Thrown/routing errors not caught lower (e.g. above BoundaryLayout)
        // render the styled "something went wrong" page instead of React
        // Router's bare default boundary. Unmatched URLs are handled by the
        // `path: '*'` 404 route below, not here.
        errorElement: <RouteError />,
        children: [
            {
                path: '/login',
                element: <LoginPage />,
            },
            {
                element: <BoundaryLayout />,
                errorElement: <RouteError />,
                children: [
                    {
                        index: true,
                        element: (
                            <ProtectedRoute>
                                <HomePage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'clients',
                        element: (
                            <ProtectedRoute>
                                <ClientsPage />
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'settings',
                        element: (
                            <ProtectedRoute>
                                <SettingsLayout />
                            </ProtectedRoute>
                        ),
                        children: [
                            {
                                path: 'user',
                                lazy: async () => {
                                    const { UserSettingsPage } = await import(
                                        '@/pages/settings/UserSettingsPage'
                                    );
                                    return { Component: UserSettingsPage };
                                },
                            },
                            {
                                path: 'organisation',
                                lazy: async () => {
                                    const { OrganisationSettingsPage } =
                                        await import(
                                            '@/pages/settings/OrganisationSettingsPage'
                                        );
                                    return { Component: OrganisationSettingsPage };
                                },
                            },
                        ],
                    },
                    // Profiler questionnaire — PUBLIC (client-facing /profiler/:slug
                    // link, no Auth0 session). Not wrapped in ProtectedRoute.
                    // ProfilerLayout swaps to a mobile viewport for this surface.
                    {
                        path: 'profiler/:slug',
                        lazy: async () => {
                            const { ProfilerLayout } = await import(
                                '@/layouts/ProfilerLayout'
                            );
                            return { Component: ProfilerLayout };
                        },
                        children: [
                            {
                                index: true,
                                lazy: async () => {
                                    const { ProfilerIndexRedirect } = await import(
                                        '@/pages/profiler/ProfilerIndexRedirect'
                                    );
                                    return { Component: ProfilerIndexRedirect };
                                },
                            },
                            {
                                path: 'intro',
                                lazy: async () => {
                                    const { ProfilerIntroPage } = await import(
                                        '@/pages/profiler/ProfilerIntroPage'
                                    );
                                    return { Component: ProfilerIntroPage };
                                },
                            },
                            {
                                path: 'activities',
                                lazy: async () => {
                                    const { ProfilerActivitiesPage } = await import(
                                        '@/pages/profiler/ProfilerActivitiesPage'
                                    );
                                    return { Component: ProfilerActivitiesPage };
                                },
                            },
                            {
                                path: 'activity-scenarios',
                                lazy: async () => {
                                    const { ProfilerActivityScenariosPage } =
                                        await import(
                                            '@/pages/profiler/ProfilerActivityScenariosPage'
                                        );
                                    return {
                                        Component: ProfilerActivityScenariosPage,
                                    };
                                },
                            },
                            {
                                path: 'interests',
                                lazy: async () => {
                                    const { ProfilerInterestsPage } = await import(
                                        '@/pages/profiler/ProfilerInterestsPage'
                                    );
                                    return { Component: ProfilerInterestsPage };
                                },
                            },
                            {
                                path: 'topics',
                                lazy: async () => {
                                    const { ProfilerTopicsPage } = await import(
                                        '@/pages/profiler/ProfilerTopicsPage'
                                    );
                                    return { Component: ProfilerTopicsPage };
                                },
                            },
                            {
                                path: 'advisor-scenario',
                                lazy: async () => {
                                    const { ProfilerAdvisorScenarioPage } =
                                        await import(
                                            '@/pages/profiler/ProfilerAdvisorScenarioPage'
                                        );
                                    return {
                                        Component: ProfilerAdvisorScenarioPage,
                                    };
                                },
                            },
                            {
                                path: 'impact-on-future',
                                lazy: async () => {
                                    const { ProfilerImpactOnFuturePage } =
                                        await import(
                                            '@/pages/profiler/ProfilerImpactOnFuturePage'
                                        );
                                    return { Component: ProfilerImpactOnFuturePage };
                                },
                            },
                            {
                                path: 'family',
                                lazy: async () => {
                                    const { ProfilerFamilyPage } = await import(
                                        '@/pages/profiler/ProfilerFamilyPage'
                                    );
                                    return { Component: ProfilerFamilyPage };
                                },
                            },
                            {
                                path: 'anything-else',
                                lazy: async () => {
                                    const { ProfilerAnythingElsePage } =
                                        await import(
                                            '@/pages/profiler/ProfilerAnythingElsePage'
                                        );
                                    return { Component: ProfilerAnythingElsePage };
                                },
                            },
                            {
                                path: 'summary',
                                lazy: async () => {
                                    const { ProfilerSummaryPage } = await import(
                                        '@/pages/profiler/ProfilerSummaryPage'
                                    );
                                    return { Component: ProfilerSummaryPage };
                                },
                            },
                        ],
                    },
                    // OAuth callbacks — protected (exchange the provider code
                    // using the user's Auth0 token). Lazy to keep them out of
                    // the app shell bundle.
                    {
                        path: 'addepar/callback',
                        lazy: async () => {
                            const { AddeparCallbackPage } = await import(
                                '@/pages/AddeparCallbackPage'
                            );
                            return {
                                Component: () => (
                                    <ProtectedRoute>
                                        <AddeparCallbackPage />
                                    </ProtectedRoute>
                                ),
                            };
                        },
                    },
                    {
                        path: 'fundment/callback',
                        lazy: async () => {
                            const { FundmentCallbackPage } = await import(
                                '@/pages/FundmentCallbackPage'
                            );
                            return {
                                Component: () => (
                                    <ProtectedRoute>
                                        <FundmentCallbackPage />
                                    </ProtectedRoute>
                                ),
                            };
                        },
                    },
                    {
                        element: <ClientLayout />,
                        children: [
                            {
                                path: 'client/create',
                                element: (
                                    <ProtectedRoute>
                                        <CreateClientPage />
                                    </ProtectedRoute>
                                ),
                            },
                            {
                                path: 'client/edit/:clientId',
                                element: (
                                    <ProtectedRoute>
                                        <EditClientPage />
                                    </ProtectedRoute>
                                ),
                            },
                            {
                                path: 'client/:clientId',
                                // Lazy-loaded to code-split the large client-detail
                                // vertical out of the app shell bundle.
                                lazy: async () => {
                                    const { ClientDetailPage } = await import(
                                        '@/pages/ClientDetailPage'
                                    );
                                    return {
                                        Component: () => (
                                            <ProtectedRoute>
                                                <ClientDetailPage />
                                            </ProtectedRoute>
                                        ),
                                    };
                                },
                                // Modal routes rendered over the client page via
                                // ClientDetailPage's <Outlet/>.
                                children: [
                                    {
                                        path: 'generate-portfolio',
                                        lazy: async () => {
                                            const { GeneratePortfolioLayout } =
                                                await import(
                                                    '@/layouts/GeneratePortfolioLayout'
                                                );
                                            return {
                                                Component: GeneratePortfolioLayout,
                                            };
                                        },
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const {
                                                        GeneratePortfolioPage,
                                                    } = await import(
                                                        '@/pages/GeneratePortfolioPage'
                                                    );
                                                    return {
                                                        Component:
                                                            GeneratePortfolioPage,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'etcho-blend/:clientEtchoPropositionBlendId',
                                                lazy: async () => {
                                                    const { EtchoBlendPage } =
                                                        await import(
                                                            '@/pages/EtchoBlendPage'
                                                        );
                                                    return {
                                                        Component: EtchoBlendPage,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'propositions',
                                        lazy: async () => {
                                            const { PropositionsLayout } =
                                                await import(
                                                    '@/layouts/PropositionsLayout'
                                                );
                                            return { Component: PropositionsLayout };
                                        },
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const { PropositionsPage } =
                                                        await import(
                                                            '@/pages/PropositionsPage'
                                                        );
                                                    return {
                                                        Component: PropositionsPage,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'portfolio-analysis/:portfolioAnalysisId',
                                        lazy: async () => {
                                            const { PortfolioAnalysisLayout } =
                                                await import(
                                                    '@/layouts/PortfolioAnalysisLayout'
                                                );
                                            return {
                                                Component: PortfolioAnalysisLayout,
                                            };
                                        },
                                        children: [
                                            {
                                                index: true,
                                                lazy: async () => {
                                                    const {
                                                        PortfolioAnalysisPage,
                                                    } = await import(
                                                        '@/pages/PortfolioAnalysisPage'
                                                    );
                                                    return {
                                                        Component:
                                                            PortfolioAnalysisPage,
                                                    };
                                                },
                                            },
                                            {
                                                path: 'company-story/:companyStoryId',
                                                lazy: async () => {
                                                    const { CompanyStoryPage } =
                                                        await import(
                                                            '@/pages/CompanyStoryPage'
                                                        );
                                                    return {
                                                        Component:
                                                            CompanyStoryPage,
                                                    };
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        path: 'portfolio/:portfolioId/make-available-on-fundment',
                                        lazy: async () => {
                                            const {
                                                MakeAvailableOnFundmentPage,
                                            } = await import(
                                                '@/pages/MakeAvailableOnFundmentPage'
                                            );
                                            return {
                                                Component:
                                                    MakeAvailableOnFundmentPage,
                                            };
                                        },
                                    },
                                    {
                                        path: 'portfolio/:portfolioId/link-fundment-accounts',
                                        lazy: async () => {
                                            const { LinkFundmentAccountsPage } =
                                                await import(
                                                    '@/pages/LinkFundmentAccountsPage'
                                                );
                                            return {
                                                Component:
                                                    LinkFundmentAccountsPage,
                                            };
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            // Catch-all 404 — any URL that matches no route above, including a
            // misconfigured OAuth redirect that resolves to a relative path.
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
]);
